import axiosInstance from '../config/axiosInstance';

export interface TrainingEntryView {
    id: string;
    userId: string;
    trainerId: string;
    trainingDate: string;
    title: string;
    description: string | null;
    activityLink: string | null;
    userComment: string | null;
    sport: string | null;
    /** Entry exists only to host comments on a day with no exercise assigned. */
    isPlaceholder: boolean;
    completed: boolean;
    durationHours: number | null;
    createdAt: string;
    updatedAt: string;
}

export interface TrainingDayView {
    date: string;
    dayName: string;
    isToday: boolean;
    isPast: boolean;
    isRestDay: boolean;
    isEditableForTrainer: boolean;
    entries: TrainingEntryView[];
}

export interface TrainingWeekResponse {
    user: {
        id: string;
        firstName: string;
        lastName: string;
        email: string;
        restDay: string | null;
    };
    weekStart: string;
    weekEnd: string;
    programLabel: string | null;
    stats: {
        totalSessions: number;
        completedSessions: number;
        upcomingSessions: number;
    };
    days: TrainingDayView[];
}

export interface TrainingComment {
    id: string;
    entryId: string;
    userId: string;
    senderName: string;
    senderRole: string;
    message: string;
    createdAt: string;
}

/** One comment thread still waiting on a trainer reply. */
export interface PendingReply {
    entryId: string;
    userId: string;
    athleteName: string;
    trainingDate: string;
    entryTitle: string;
    count: number;
    lastMessage: string;
    /** The unanswered athlete messages, oldest first. */
    messages: { id: string; message: string; createdAt: string }[];
    waitingSince: string;
    lastCommentAt: string;
}

export interface UpsertWeekPayload {
    days: Array<{
        id?: string;
        date: string;
        title?: string;
        description?: string;
        activityLink?: string;
        sport?: string;
    }>;
    programLabel?: string;
}

const trainingService = {
    async getPendingReplies(): Promise<PendingReply[]> {
        const response = await axiosInstance.get('/training/pending-replies');
        return response.data;
    },

    async markAthleteAnswered(userId: string): Promise<void> {
        await axiosInstance.post(`/training/pending-replies/users/${userId}/mark-answered`);
        window.dispatchEvent(new Event('comment-posted'));
    },

    async getUserWeek(userId: string, date: string): Promise<TrainingWeekResponse> {
        const response = await axiosInstance.get(`/training/users/${userId}/week`, {
            params: { date },
        });
        return response.data;
    },

    async upsertUserWeek(userId: string, date: string, payload: UpsertWeekPayload): Promise<TrainingWeekResponse> {
        const response = await axiosInstance.put(`/training/users/${userId}/week`, payload, {
            params: { date },
        });
        return response.data;
    },

    async getComments(entryId: string): Promise<TrainingComment[]> {
        const response = await axiosInstance.get(`/training/entries/${entryId}/comments`);
        return response.data;
    },

    async addComment(entryId: string, message: string): Promise<TrainingComment> {
        const response = await axiosInstance.post(`/training/entries/${entryId}/comments`, { message });
        window.dispatchEvent(new Event('comment-posted'));
        return response.data;
    },

    async deleteComment(commentId: string): Promise<void> {
        await axiosInstance.delete(`/training/comments/${commentId}`);
    },

    /**
     * Comment on a day with no exercise assigned. The backend creates a
     * placeholder entry to host the thread.
     */
    async addDayComment(userId: string, date: string, message: string): Promise<TrainingComment> {
        const response = await axiosInstance.post(`/training/users/${userId}/day-comments`, {
            date,
            message,
        });
        window.dispatchEvent(new Event('comment-posted'));
        return response.data;
    },
};

export default trainingService;
